import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditLegacyProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProviderLegacy";
import {
  DeleteButton,
  LineStringButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { getCurrentProjectData } from "fond/project";
import { featureCollection } from "fond/turf";
import { LayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<LineStringButton key="drawLine" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

/**
 * This component is responsible for managing the drawing of planner extended blockers.
 *
 * Each blocker carries a boolean attribute per tier it applies to; a newly drawn blocker applies to every tier
 * until the user unticks tiers in the attribute prompt.
 */
const BlockersDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));

  const defaultOptions: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      editOptions: {
        allLayers: [
          {
            ID: LayerIds.blocker,
            GeometryType: "LineString",
            getFeatureProperties: () => ({
              layerId: LayerIds.blocker,
              Drop: "true",
              DropTail: "true",
              Dist: "true",
              Feeder: "true",
            }),
            Attributes: {
              Drop: { Type: "BOOLEAN" },
              DropTail: { Type: "BOOLEAN" },
              Dist: { Type: "BOOLEAN" },
              Feeder: { Type: "BOOLEAN" },
            },
            PromptAttributesOnCreate: true,
          },
        ],
        editingLayerId: LayerIds.blocker,
        editingLayerIds: [LayerIds.blocker],
      },
    }),
    []
  );

  // Get features from Redux (same source as MapContent) to ensure IDs match what's on the map
  const initialFeatures = useMemo(() => {
    const features = projectData?.layers?.[LayerIds.blocker]?.features;
    return features?.length ? featureCollection(features) : undefined;
  }, [projectData]);

  return (
    <MultiLayerEditLegacyProvider versionId={versionId} options={defaultOptions} config={allToolConfig} initialFeatures={initialFeatures}>
      <MultiLayerEditPanelContent title="Edit blockers" alertTitle="Draw lines to block cable path." />
    </MultiLayerEditLegacyProvider>
  );
};

export default BlockersDraw;
